/**
 * MongoDB 用户登录页面
 * 零侵入：完全独立于现有 Login.js
 * 路由：/login-v2
 */

import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  Link,
  Grid,
  Checkbox,
  FormControlLabel,
  Divider,
  Alert,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  AccountCircle as AccountCircleIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Stars as StarsIcon,
  Info as InfoIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';

const MongoLogin = () => {
  // 表单状态
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  
  // 认证 Store
  const { 
    mongoLogin, 
    loading, 
    error, 
    mongoAvailable,
    switchAuthSystem,
    isLoggedInMongo,
    mongoUser
  } = useAuthStore();
  
  const history = useHistory();

  // 如果已经登录，直接跳转到个人主页
  React.useEffect(() => {
    if (isLoggedInMongo && mongoUser && mongoUser.user_id) {
      console.log('检测到已登录状态，跳转到个人主页');
      history.replace('/profile-v2');
    }
  }, [isLoggedInMongo, mongoUser, history]);

  // 提交登录
  const handleLogin = async (event) => {
    event.preventDefault();

    if (!email.trim() || !password) {
      return;
    }

    console.log('开始登录...', email);
    const result = await mongoLogin(email, password);
    console.log('登录结果:', result);

    if (result.success) {
      console.log('登录成功，100ms后跳转到个人主页...');
      // 登录成功，延迟一小段时间确保状态更新完成，然后跳转
      setTimeout(() => {
        history.replace('/profile-v2');
      }, 100);
    } else {
      console.error('登录失败:', result.error);
    }
  };

  // 切换到旧系统
  const handleSwitchToLegacy = () => {
    switchAuthSystem('json');
    history.push('/login');
  };

  return (
    <Container component="main" maxWidth="xs">
      <Paper sx={{ mt: 8, p: 4 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {/* MongoDB 徽章 */}
          <Chip
            icon={<StarsIcon />}
            label="MongoDB 新系统"
            color="primary"
            variant="outlined"
            sx={{ mb: 2 }}
          />
          
          <AccountCircleIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          <Typography component="h1" variant="h5">
            用户登录
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            新用户系统 · 支持会员等级和积分成长
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleLogin} sx={{ mt: 2 }}>
          {/* MongoDB 连接状态警告 */}
          {!mongoAvailable && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon fontSize="small" />
                <Typography variant="body2">
                  MongoDB 服务暂时不可用
                  <Link
                    component="button"
                    variant="body2"
                    onClick={handleSwitchToLegacy}
                    sx={{ ml: 1 }}
                  >
                    切换到旧系统登录
                  </Link>
                </Typography>
              </Box>
            </Alert>
          )}

          {/* 错误提示 */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* 邮箱 */}
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="邮箱地址"
            name="email"
            autoComplete="email"
            autoFocus
            disabled={loading || !mongoAvailable}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            InputProps={{
              startAdornment: <AccountCircleIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />

          {/* 密码 */}
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="密码"
            type={showPassword ? 'text' : 'password'}
            id="password"
            autoComplete="current-password"
            disabled={loading || !mongoAvailable}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{
              startAdornment: <LockIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              endAdornment: (
                <Button
                  onClick={() => setShowPassword(!showPassword)}
                  sx={{ minWidth: 0 }}
                  tabIndex={-1}
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </Button>
              ),
            }}
          />

          {/* 记住我 */}
          <FormControlLabel
            control={
              <Checkbox
                value="remember"
                color="primary"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={loading || !mongoAvailable}
              />
            }
            label="记住我"
          />

          {/* 登录按钮 */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 2, mb: 2 }}
            disabled={loading || !mongoAvailable}
          >
            {loading ? '登录中...' : '登录'}
          </Button>

          <Divider sx={{ my: 2 }}>
            <Chip label="或者" size="small" />
          </Divider>

          <Grid container>
            <Grid item xs>
              <Link href="#" variant="body2">
                忘记密码？
              </Link>
            </Grid>
            <Grid item>
              <Link component={RouterLink} to="/register-v2" variant="body2">
                没有账户？立即注册
              </Link>
            </Grid>
          </Grid>

          <Grid container sx={{ mt: 2 }}>
            <Grid item xs>
              <Tooltip title="使用 JSON 文件存储的旧系统">
                <Link
                  component={RouterLink}
                  to="/login"
                  variant="body2"
                  color="text.secondary"
                  onClick={() => switchAuthSystem('json')}
                >
                  <ArrowBackIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  返回旧系统登录
                </Link>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* 新系统特性介绍 */}
      <Paper sx={{ mt: 3, p: 3 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
          🔐 安全升级
        </Typography>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              新系统采用 SHA256 + 随机盐值加密存储密码，比旧系统的明文存储更加安全。
              同时支持会员等级、积分成长、观看历史等高级功能。
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default MongoLogin;
